import { ASH_ADMIN } from '@/constants/clientPortalRoles'
import {
  findIdentityScopeByNormalizedPartnerId,
  getRolesFromScope,
  getRolesFromScopes,
  hasNonPhiRole,
  rolesInclude,
  type IdentityScopes,
} from '@/utilities/auth/identityScopeRoles'

export type PartnerScopedPhiRoleAccess = {
  hasAllowedAdminRole: boolean
  isPhiUser: boolean
}

export type AshAdminNonPhiScope = 'active-partner' | 'all-scopes'

type ResolvePartnerScopedPhiRoleAccessInput = {
  scopes: IdentityScopes | null | undefined
  partnerId: string | null | undefined
  allowedPartnerRoles: readonly string[]
  /**
   * Roles on the active partner that still count as an allowed admin when they are non-PHI.
   * `isPhiUser` stays false for these roles.
   */
  nonPhiAdminRoles?: readonly string[]
  /**
   * Roles that the Ash admin PHI check reads.
   * `active-partner` reads only the active partner.
   * `all-scopes` reads every scope.
   */
  ashAdminNonPhiScope: AshAdminNonPhiScope
}

function isPhiUserForRoles(roles: readonly string[], allowEmptyRoles: boolean): boolean {
  if (roles.length === 0) return allowEmptyRoles
  return !hasNonPhiRole(roles)
}

/**
 * Resolve admin access and PHI access for one partner.
 * An Ash admin role on any scope sets `hasAllowedAdminRole` to true.
 * `allowedPartnerRoles` applies only to the active partner.
 */
export function resolvePartnerScopedPhiRoleAccess({
  scopes,
  partnerId,
  allowedPartnerRoles,
  nonPhiAdminRoles = [],
  ashAdminNonPhiScope,
}: ResolvePartnerScopedPhiRoleAccessInput): PartnerScopedPhiRoleAccess {
  const partnerRoles = getRolesFromScope(
    findIdentityScopeByNormalizedPartnerId(scopes, partnerId) ?? {},
  )
  const allRoles = getRolesFromScopes(scopes)

  if (rolesInclude(allRoles, [ASH_ADMIN])) {
    const phiRoles = ashAdminNonPhiScope === 'all-scopes' ? allRoles : partnerRoles

    return {
      hasAllowedAdminRole: true,
      isPhiUser: isPhiUserForRoles(phiRoles, ashAdminNonPhiScope === 'active-partner'),
    }
  }

  const hasAllowedPartnerRole = rolesInclude(partnerRoles, allowedPartnerRoles)
  const hasNonPhiAdminRole = rolesInclude(partnerRoles, nonPhiAdminRoles)

  return {
    hasAllowedAdminRole: hasAllowedPartnerRole || hasNonPhiAdminRole,
    isPhiUser: isPhiUserForRoles(partnerRoles, false),
  }
}
